import { defineComponent } from 'vue'

import { GraphqlMutationResponse, GraphqlQueryResponse } from '~/types/apolloTypes'

import publicReportListOwner from '@/apollo/queries/owner/publicReportListOwner.gql'
import publicReportAnalyzerDistribution from '@/apollo/queries/reports/publicReportAnalyzerDistribution.gql'
import publicReportBase from '@/apollo/queries/reports/publicReportBase.gql'
import publicReportBaseReport from '@/apollo/queries/reports/publicReportBaseReport.gql'
import publicReportCategoryDistribution from '@/apollo/queries/reports/publicReportCategoryDistribution.gql'
import publicReportComplianceIssues from '@/apollo/queries/reports/publicReportComplianceIssues.gql'
import publicReportHistoricValues from '@/apollo/queries/reports/publicReportHistoricalValues.gql'
import publicReportRecentStats from '@/apollo/queries/reports/publicReportRecentStats.gql'
import publicReportListRepo from '@/apollo/queries/repository/publicReportListRepo.gql'

import createPublicReport from '@/apollo/mutations/reports/createPublicReport.gql'
import deletePublicReport from '@/apollo/mutations/reports/deletePublicReport.gql'
import verifyPasswordForPublicReport from '@/apollo/mutations/reports/verifyPasswordForPublicReport.gql'

import PaginationMixin from './paginationMixin'
import ReportMixin from './reportMixin'

import {
  HistoricalValues,
  IssueDistributionT,
  PublicReportErrors,
  ReportMeta,
  ReportMetaProperties,
  ReportPageT,
  ReportSortT,
  ReportToEditT
} from '~/types/reportTypes'
import {
  ComplianceIssue,
  ComplianceIssueOccurrence,
  CreatePublicReportInput,
  IssueDistribution,
  PublicReport,
  PublicReportBaseReportQueryVariables,
  PublicReportRecentStatsQueryVariables,
  RecentStat,
  Report,
  ReportSource,
  VerifyPasswordForPublicReportInput
} from '~/types/types'
import { resolveNodes } from '~/utils/array'
import { getDateRange } from '~/utils/date'
import { prepareLabels } from '~/utils/reports'
import MetaMixin from './metaMixin'

const EMPTY_REPORT: ReportToEditT = {
  reportId: '',
  isRestricted: false,
  reportKeys: [],
  shareHistoricalData: false,
  label: '',
  source: ReportSource.SourceAll
}
const REPORT_READY_DELAY = 3000

export default defineComponent({
  name: 'PublicReportMixin',
  mixins: [ReportMixin, PaginationMixin, MetaMixin],

  data() {
    return {
      isMutateReportModalOpen: false,
      errorMessage: null as PublicReportErrors | null,
      q: '',
      sort: '' as ReportSortT | '',
      reportsListLoading: false,
      reportsList: [] as Array<PublicReport>,
      perPageCount: 20,
      editMode: false,
      reportSaveLoading: false,
      reportToEdit: EMPTY_REPORT,
      showConfirmDelete: false,
      reportIdToDelete: '',
      reportLabelToDelete: '',
      deleteLoading: false,
      showCreateSuccessModal: false,
      createSuccessModalEditMode: false,
      newReportCreated: null as PublicReport | null,
      newReportCreatedPassword: '',
      submitPasswordLoading: false,
      isReportReady: false
    }
  },

  computed: {
    showCtaAndControls(): boolean {
      return this.reportsList.length > 0 || this.q !== '' || this.reportsListLoading
    },

    publicReportMeta() {
      return ReportMeta[ReportPageT.PUBLIC_REPORTS]
    }
  },

  methods: {
    async clearSearch() {
      this.q = ''
      this.currentPage = 1
      await this.fetchPublicReportList()
    },

    async handleSortChange() {
      this.currentPage = 1
      await this.fetchPublicReportList(false)
    },

    async handlePublicReportSearch(query: string) {
      this.q = query
      this.currentPage = 1
      await this.fetchPublicReportList(false)
    },

    triggerDelete(reportId: string, label: string) {
      this.showConfirmDelete = true
      this.reportLabelToDelete = label
      this.reportIdToDelete = reportId
    },

    triggerEdit(reportId: string) {
      this.editMode = true

      const report = this.reportsList.find((report) => report.reportId === reportId)
      if (report) {
        this.reportToEdit = report
      } else {
        this.reportToEdit = EMPTY_REPORT
      }

      this.isMutateReportModalOpen = true
    },

    async triggerExport(reportId: string) {
      this.$toast.show({
        type: 'info',
        message: 'Preparing report for export, the download will start shortly.',
        timeout: 6
      })

      const report = this.reportsList.find((report) => report.reportId === reportId)
      if (report) {
        try {
          const response = await fetch('/server/export-report', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: reportId, labels: report.reportKeys })
          })

          if (!response.ok) {
            throw new Error('Failed to export report')
          }

          const fileContents = await response.blob()
          const aTag = document.createElement('a')
          const url = URL.createObjectURL(fileContents)
          aTag.href = url
          aTag.download = `DeepSource-Reports-${reportId}.zip`
          aTag.click()
          URL.revokeObjectURL(url)
        } catch (e) {
          this.$logErrorAndToast(e as Error, 'Unable to export report. Please contact support.')
        }
      }
    },

    triggerModalClose() {
      this.isMutateReportModalOpen = false
      this.editMode = false
      this.reportToEdit = EMPTY_REPORT
    },

    triggerSuccessModalClose() {
      this.showCreateSuccessModal = false
      this.createSuccessModalEditMode = false
      this.newReportCreated = null
      this.newReportCreatedPassword = ''
    },

    setPageMetaData(reportName: ReportMetaProperties['title'], ownerLogin: string) {
      this.metaTitle = `${reportName} — ${ownerLogin}`
      this.metaDescription = `${reportName} report for ${ownerLogin}, generated by DeepSource.`
    },

    async fetchPublicReportList(refetch = true) {
      const ownerPublicReportsRoute = 'orgs-provider-owner-reports-public-reports'
      const repoPublicReportsRoute = 'provider-owner-repo-reports-public-reports'

      if (
        this.$route.name !== ownerPublicReportsRoute &&
        this.$route.name !== repoPublicReportsRoute
      ) {
        return
      }

      const first = this.perPageCount
      const offset = this.queryOffset
      const sort = this.sort
      const searchQuery = this.q

      if (this.$route.name === ownerPublicReportsRoute) {
        this.reportsListLoading = true

        const { provider, owner: login } = this.$route.params

        try {
          const response = (await this.$fetchGraphqlData(
            publicReportListOwner,
            {
              login,
              provider: this.$providerMetaMap[provider].value,
              first,
              offset,
              sort,
              q: searchQuery
            },
            refetch
          )) as GraphqlQueryResponse

          this.totalCount = response.data.owner?.publicReports?.totalCount ?? 0
          this.reportsList = resolveNodes(response.data.owner?.publicReports)
        } catch (e) {
          this.$logErrorAndToast(
            e as Error,
            'Unable to fetch public reports. Please contact support.'
          )
        } finally {
          this.reportsListLoading = false
        }
      } else if (this.$route.name === repoPublicReportsRoute) {
        this.reportsListLoading = true

        const { provider, owner, repo } = this.$route.params

        try {
          const response = (await this.$fetchGraphqlData(
            publicReportListRepo,
            {
              name: repo,
              owner,
              provider: this.$providerMetaMap[provider].value,
              first,
              offset,
              sort,
              q: searchQuery
            },
            refetch
          )) as GraphqlQueryResponse

          this.totalCount = response.data.repository?.publicReports?.totalCount ?? 0
          this.reportsList = resolveNodes(response.data.repository?.publicReports)
        } catch (e) {
          this.$logErrorAndToast(
            e as Error,
            'Unable to fetch public reports. Please contact support.'
          )
        } finally {
          this.reportsListLoading = false
        }
      }
    },

    async fetchPublicReportBase(
      reportId: string,
      token?: string,
      refetch?: boolean
    ): Promise<PublicReport | void> {
      this.submitPasswordLoading = true

      try {
        const response = (await this.$fetchGraphqlData(
          publicReportBase,
          {
            reportId,
            token
          },
          refetch
        )) as GraphqlQueryResponse

        if (response.data.publicReport?.id) {
          this.errorMessage = null
        }

        return response.data.publicReport as PublicReport
      } catch (e) {
        const message = (e as Error).message.replace('GraphQL error: ', '')
        if (message === PublicReportErrors.AUTH_REQUIRED) {
          this.errorMessage = PublicReportErrors.AUTH_REQUIRED
        }

        switch (message) {
          case PublicReportErrors.AUTH_REQUIRED:
            this.errorMessage = PublicReportErrors.AUTH_REQUIRED
            break
          case PublicReportErrors.INVALID_TOKEN:
            this.errorMessage = PublicReportErrors.INVALID_TOKEN
            break
          default:
            this.$logErrorAndToast(e as Error, 'Unable to verify password. Please contact support.')
            break
        }
      } finally {
        this.submitPasswordLoading = false
      }
    },

    async fetchPublicReportBaseReport(args: PublicReportBaseReportQueryVariables): Promise<void> {
      this.reportsDataLoading = true
      try {
        const response = (await this.$fetchGraphqlData(
          publicReportBaseReport,
          args
        )) as GraphqlQueryResponse

        this.report = response.data.publicReport?.report as Report
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'Unable to fetch report details. Please contact support.'
        )
      } finally {
        this.reportsDataLoading = false
      }
    },

    async fetchPublicReportRecentStats(args: PublicReportRecentStatsQueryVariables): Promise<void> {
      this.recentStatsLoading = true

      try {
        const response = (await this.$fetchGraphqlData(
          publicReportRecentStats,
          args
        )) as GraphqlQueryResponse

        this.recentStats = response.data.publicReport?.report?.recentStats as Array<RecentStat>
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'Unable to fetch recent stats of report, please contact support.'
        )
      } finally {
        this.recentStatsLoading = false
      }
    },

    async fetchPublicReportDistributionStats(
      key: ReportPageT,
      reportId: string,
      distributionType: IssueDistributionT,
      token?: string
    ): Promise<IssueDistribution[]> {
      this.distributionStatsLoading = true

      const distributionQuery =
        distributionType === IssueDistributionT.ANALYZER
          ? publicReportAnalyzerDistribution
          : publicReportCategoryDistribution

      try {
        const response = (await this.$fetchGraphqlData(distributionQuery, {
          key,
          reportId,
          token
        })) as GraphqlQueryResponse

        return (
          distributionType === IssueDistributionT.ANALYZER
            ? response.data.publicReport?.issueDistributionByAnalyzer
            : response.data.publicReport?.issueDistributionByCategory
        ) as Array<IssueDistribution>
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'Unable to fetch issue distribution, please contact support.'
        )
      } finally {
        this.distributionStatsLoading = false
      }
      return []
    },

    async fetchPublicReportComplianceIssues(
      reportId: string,
      reportKey: ReportPageT,
      token?: string
    ): Promise<void> {
      this.complianceIssuesLoading = true
      try {
        const fetchSeverityMap = [ReportPageT.OWASP_TOP_10, ReportPageT.MISRA_C].includes(reportKey)

        const response = (await this.$fetchGraphqlData(publicReportComplianceIssues, {
          reportId,
          token,
          reportKey,
          fetchSeverityMap
        })) as GraphqlQueryResponse

        this.complianceIssueList = response.data.publicReport
          ?.complianceIssues as Array<ComplianceIssue>

        this.complianceIssuesSeverityMap = response.data.publicReport
          ?.complianceIssuesSeverityMap as ComplianceIssueOccurrence
      } catch (e) {
        this.$logErrorAndToast(
          e as Error,
          'Unable to fetch security issues list, please contact support.'
        )
      } finally {
        this.complianceIssuesLoading = false
      }
    },

    async fetchPublicReportHistoricalValues(
      reportId: string,
      reportKey: string,
      token?: string
    ): Promise<void> {
      const defaultDateRangeFilter = this.$cookies.get('reports-default-daterange-filter')

      if (this.dateRangeFilter !== defaultDateRangeFilter) {
        this.$cookies.set('reports-default-daterange-filter', this.dateRangeFilter)
      }

      const { startDate, endDate } = getDateRange(this.dateRangeFilter)

      if (reportId && reportKey && startDate && endDate) {
        this.historicalValuesLoading = true
        try {
          const response = (await this.$fetchGraphqlData(publicReportHistoricValues, {
            reportId,
            reportKey,
            token,
            startDate,
            endDate
          })) as GraphqlQueryResponse
          this.historicalValues = (await response.data.publicReport?.report
            ?.historicalValues) as HistoricalValues

          // Only setting labels in mixin cause they are common accross every page.
          // Datasets are not.
          this.labels = prepareLabels(this.historicalValues.labels, startDate, endDate)
        } catch (e) {
          this.$logErrorAndToast(
            e as Error,
            'Unable to fetch historical data of report, please contact support.'
          )
        } finally {
          this.historicalValuesLoading = false
        }
      }
    },

    async createPublicReport(
      newReportArgs: CreatePublicReportInput,
      callback?: () => void
    ): Promise<void> {
      this.reportSaveLoading = true
      try {
        const response = (await this.$applyGraphqlMutation(createPublicReport, {
          input: newReportArgs
        })) as GraphqlMutationResponse

        const newReport = response.data?.createPublicReport?.report

        if (newReport?.reportId) {
          callback?.()

          this.newReportCreated = newReport
          this.newReportCreatedPassword = newReportArgs.password ?? ''

          // Adding delay to match closing of previous modal
          setTimeout(() => {
            this.showCreateSuccessModal = true
          }, 400)

          await this.fetchPublicReportList()
        }
      } catch (e) {
        this.$logErrorAndToast(e as Error, "Can't create a report. Please contact support.")
      } finally {
        this.reportSaveLoading = false
      }
    },

    async deletePublicReport(callback?: () => void): Promise<void> {
      this.deleteLoading = true

      try {
        const response = (await this.$applyGraphqlMutation(deletePublicReport, {
          input: { reportId: this.reportIdToDelete }
        })) as GraphqlMutationResponse

        const res = response.data?.deletePublicReport
        if (res?.ok) {
          if (this.reportsList.length === 1 && this.currentPage > 1) {
            this.currentPage = this.currentPage - 1
          }
          await this.fetchPublicReportList()
          this.$toast.success('Report deleted succesfully')
          callback?.()
        }
      } catch (e) {
        const message = (e as Error).message.replace('GraphQL error: ', '')

        // If a user is trying to delete a report, but right before that someone else deletes the report,
        // it'll send back a 404, because the erportsList won't have been updated for the current user.
        // So we just give them a success message because the report was already deleted.
        if (message === PublicReportErrors.DOES_NOT_EXIST) {
          callback?.()
          this.$toast.success('Report deleted succesfully')
          await this.fetchPublicReportList()
        } else {
          this.$logErrorAndToast(e as Error, 'Unable to delete report. Please contact support.')
        }
      } finally {
        this.deleteLoading = false
      }
    },

    async verifyPasswordForPublicReport(
      input: VerifyPasswordForPublicReportInput
    ): Promise<string> {
      this.submitPasswordLoading = true

      try {
        const response = (await this.$applyGraphqlMutation(verifyPasswordForPublicReport, {
          input
        })) as GraphqlMutationResponse

        const token = response.data?.verifyPasswordForPublicReport?.token

        if (token) {
          this.errorMessage = null
        }

        return token as string
      } catch (e) {
        const message = (e as Error).message.replace('GraphQL error: ', '')

        // Not asssigning message to errorMessage directly cause need to match enum types
        switch (message) {
          case PublicReportErrors.INVALID_PASSWORD:
            this.errorMessage = PublicReportErrors.INVALID_PASSWORD
            break
          case PublicReportErrors.DOES_NOT_EXIST:
            this.errorMessage = PublicReportErrors.DOES_NOT_EXIST
            break
          case PublicReportErrors.RATE_LIMITED:
            this.errorMessage = PublicReportErrors.RATE_LIMITED
            break
          default:
            this.$logErrorAndToast(e as Error, 'Unable to verify password. Please contact support.')
            break
        }
      } finally {
        this.submitPasswordLoading = false
      }
      return ''
    },

    setReportReady() {
      setTimeout(() => {
        this.isReportReady = true
      }, REPORT_READY_DELAY)
    }
  }
})
